<template>
  <Carousel
    data-testid="carousel-article"
    :items="articles"
    with-progress-bar
    item-class="!p-4 md:!p-6"
    class="h-full"
  >
    <template #content="{ item }">
      <div class="flex flex-col gap-y-2">
        <span
          class="mb-3 flex size-8 items-center justify-center rounded-lg bg-black p-1 text-p-500"
        >
          <Icon v-if="item?.blurb" name="file-pdf" class="h-5 w-5" />
          <ArticleSmallIcon v-else-if="item?.url" class="h-5 w-5" />
          <BarLineChartIcon v-else class="h-4 w-5" />
        </span>
        <h3
          data-testid="carousel-title"
          class="text-xl font-bold leading-normal md:text-2xl"
          :class="[
            item?.blurb ? 'xl:line-clamp-2 line-clamp-3' : 'line-clamp-2',
          ]"
        >
          {{ item?.title }}
        </h3>
        <div class="flex gap-x-1 text-xs font-semibold md:text-sm">
          <div data-testid="carousel-publish-date">
            {{ item?.publishedDate }}
          </div>
          <div>
            {{ item?.readingTime ? `· ${item?.readingTime} min read` : '' }}
          </div>
        </div>
        <ClientOnly>
          <p
            v-if="item?.blurb"
            class="text-xs md:text-sm"
            :class="lineClampClass"
            v-html="item?.blurb"
          ></p>
          <p
            v-else
            class="line-clamp-1 text-xs md:text-sm"
            v-html="item?.excerpt"
          />
        </ClientOnly>
      </div>
    </template>
    <template #action="{ item }">
      <NavigationLink
        v-if="!isPdf(item)"
        :url="getArticleUrl(item)"
        :type="getNavigationTypeElement(item)"
      >
        <Button variant="clean" class="fade-in text-sm"> Read More </Button>
      </NavigationLink>
      <template v-else>
        <ReportFooter
          :report="item"
          :with-icon="false"
          @open-viewer="() => openViewer(item)"
        />
      </template>
    </template>
  </Carousel>
</template>

<script setup lang="ts">
import ReportFooter from '~/components/critical-insights/ReportFooter.vue'
import NavigationLink from '~/components/shared/navigation/Link.vue'
import Carousel from '~/components/market-insights/Carousel.vue'
import BarLineChartIcon from '~/assets/icons/bar-line-chart.svg'
import ArticleSmallIcon from '~/assets/icons/ArticleSmall.svg'
import Button from '~/components/common/Button.vue'
import { BLACK_MASS, RECYCLING } from '~/constants/services/constants'
import type { TArticle } from '~/types/graphql.type'
import { MARKET_TYPE } from '~/src/constants/markets'
import type { TReport } from '~/src/types/reports'
import { useModalStore } from '~/stores/modal'
import Icon from '~/components/common/Icon.vue'

const props = withDefaults(
  defineProps<{
    market?: string
    articles?: Array<TArticle>
    lineClampClass?: string
  }>(),
  {
    market: '',
    articles: () => [],
    lineClampClass: 'line-clamp-2',
  },
)

const { open, setModalParams } = useModalStore()

const openViewer = (report?: TReport) => {
  setModalParams({ pdfUrl: report?.file_name })
  open('pdfViewerModal')
}

const isPdf = (article: TArticle | TReport) => {
  if (!article) return false
  if ('market_type' in article)
    return article.market_type === MARKET_TYPE.CRITICAL_INSIGHTS
  return false
}

const getNavigationTypeElement = (article: TArticle) => {
  return article?.url ? 'a' : 'nuxt-link'
}

const getArticleUrl = (article: TArticle) => {
  if (!article) return ''
  if (article?.url) return article.url
  if (props.market === '') return ''
  const marketSlug =
    props.market === BLACK_MASS.slug ? RECYCLING.slug : props.market
  return article.slug ? `/${marketSlug}/${article.slug}` : ''
}
</script>
